import { useMemo } from "react";
import { Avatar, Card, StatCard, StatusPill } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { useIsMobile } from "../lib/hooks";
import type { ScheduleEvent, Student } from "../lib/types";

const DAY_LABELS = ["一", "二", "三", "四", "五", "六", "日"];
const WEEK_DAYS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getMondayOf(d: Date): Date {
  const day = d.getDay();
  const m = new Date(d);
  m.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  m.setHours(0, 0, 0, 0);
  return m;
}

interface DashboardProps {
  events: ScheduleEvent[];
  students: Student[];
  pendingLeaves: number;
}

export function Dashboard({ events, students, pendingLeaves }: DashboardProps) {
  const isMobile = useIsMobile();
  useLucideIcons([events, students, pendingLeaves]);

  const now = new Date();
  const todayStr = toDateStr(now);
  const nowTimeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const todayDow = (now.getDay() + 6) % 7; // 0=Mon … 6=Sun

  const monthHours = useMemo(
    () => events.filter(e => e.status === "taught" && e.date?.startsWith(monthPrefix))
      .reduce((s, e) => s + (e.hours ?? 0), 0),
    [events, monthPrefix],
  );

  const leaveHours = useMemo(
    () => events.filter(e => e.status === "leave" && e.date?.startsWith(monthPrefix))
      .reduce((s, e) => s + (e.hours ?? 0), 0),
    [events, monthPrefix],
  );

  const activeCount = useMemo(
    () => students.filter(s => s.status === "upcoming").length,
    [students],
  );

  const trend = useMemo(() => {
    const mon = getMondayOf(now);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      const date = toDateStr(d);
      const taught = events.filter(e => e.date === date && e.status === "taught")
        .reduce((s, e) => s + (e.hours ?? 0), 0);
      const upcoming = events.filter(e => e.date === date && e.status === "upcoming")
        .reduce((s, e) => s + (e.hours ?? 0), 0);
      return { label: DAY_LABELS[i], taught, upcoming, v: taught + upcoming };
    });
  }, [events]);

  const todayEvents = useMemo(
    () => events.filter(e => e.date === todayStr).sort((a, b) => a.slot.localeCompare(b.slot)),
    [events, todayStr],
  );

  const kpis = [
    {
      label: "本月课时",
      value: monthHours % 1 === 0 ? String(monthHours) : monthHours.toFixed(1),
      unit: "课时",
      delta: "本月已上",
      trend: "flat" as const,
      icon: "timer",
    },
    {
      label: "本月请假",
      value: leaveHours % 1 === 0 ? String(leaveHours) : leaveHours.toFixed(1),
      unit: "课时",
      delta: leaveHours > 0 ? "有请假" : "无请假",
      trend: leaveHours > 0 ? ("up" as const) : ("flat" as const),
      accent: "accent" as const,
      icon: "calendar-x",
    },
    {
      label: "在读学员",
      value: String(activeCount),
      unit: "人",
      delta: students.length > 0 ? `共 ${students.length} 人` : "暂无学员",
      trend: "flat" as const,
      icon: "users",
    },
    {
      label: "待审批请假",
      value: String(pendingLeaves),
      unit: "条",
      delta: pendingLeaves > 0 ? "待处理" : "已全部处理",
      trend: "flat" as const,
      accent: pendingLeaves > 0 ? ("accent" as const) : undefined,
      icon: "alarm-clock",
    },
  ];

  const maxV = Math.max(...trend.map(t => t.v), 1);

  const subtitleDate = `${now.getMonth() + 1}月${now.getDate()}日 ${WEEK_DAYS[now.getDay()]}`;
  const monday = getMondayOf(now);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const trendSubtitle = `本周课时 · ${monday.getMonth() + 1}月${monday.getDate()}日 – ${sunday.getMonth() + 1}月${sunday.getDate()}日`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: isMobile ? 10 : 16 }}>
        {kpis.map((k) => (
          <StatCard
            key={k.label}
            label={k.label}
            value={k.value}
            unit={k.unit}
            delta={k.delta}
            trend={k.trend}
            accent={k.accent}
            icon={<Icon name={k.icon} size={18} />}
          />
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.55fr 1fr", gap: isMobile ? 12 : 20 }}>
        <Card title="本周课时趋势" subtitle={trendSubtitle}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: isMobile ? 10 : 18, height: isMobile ? 130 : 184, padding: "12px 4px 0" }}>
            {trend.map((t, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  height: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>
                  {t.v > 0 ? (t.v % 1 === 0 ? t.v : t.v.toFixed(1)) : ""}
                </span>
                <div style={{
                  width: "100%",
                  maxWidth: 38,
                  height: Math.max((t.v / maxV) * 130, t.v > 0 ? 4 : 0),
                  borderRadius: "8px 8px 4px 4px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  opacity: t.v === 0 ? 0.15 : 1,
                }}>
                  {t.upcoming > 0 && (
                    <div style={{
                      flex: t.upcoming,
                      background: i === todayDow ? "color-mix(in srgb, var(--accent) 45%, transparent)" : "color-mix(in srgb, var(--brand) 35%, transparent)",
                    }} />
                  )}
                  {t.taught > 0 && (
                    <div style={{
                      flex: t.taught,
                      background: i === todayDow ? "var(--accent)" : "var(--teal-500)",
                    }} />
                  )}
                  {t.v === 0 && (
                    <div style={{ flex: 1, background: i === todayDow ? "var(--accent)" : "var(--teal-500)" }} />
                  )}
                </div>
                <span style={{
                  fontSize: 12,
                  color: i === todayDow ? "var(--accent)" : "var(--text-subtle)",
                  fontWeight: i === todayDow ? 700 : 400,
                }}>
                  {t.label}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="今日课程" subtitle={subtitleDate}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {todayEvents.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-subtle)", padding: "10px 6px" }}>今日暂无课程。</div>
            ) : (
              todayEvents.map((r, i) => (
                <div
                  key={r.id ?? i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 6px",
                    borderBottom: i < todayEvents.length - 1 ? "1px solid var(--divider)" : "none",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-num)",
                      fontWeight: 800,
                      fontSize: 14,
                      color: "var(--text-strong)",
                      width: 44,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {r.slot}
                  </span>
                  {(() => {
                    const isGroup = (r.studentIds?.length ?? 0) > 1;
                    const memberNames = isGroup
                      ? r.studentIds!.map(id => students.find(s => s.id === id)?.name ?? "").filter(Boolean)
                      : [r.name];
                    return (
                      <>
                        <Avatar name={memberNames[0] ?? r.name} size="sm" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {memberNames.join("、")}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{r.subject}{isGroup ? ` · ${memberNames.length}人` : ""}</div>
                        </div>
                      </>
                    );
                  })()}
                  <StatusPill
                    status={r.status === "upcoming" && r.end_time <= nowTimeStr ? "taught" : r.status}
                    dot={false}
                  />
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
